import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'components/shared/DataTable';
import TablePagination from 'components/shared/Pagination/TablePagination';
import { ChevronDownIcon } from 'icons/ChevronIcon';
import usePageTracking from 'utils/hooks/usePageTracking';
import { usePagination } from 'utils/hooks/usePagination';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { UserSessionSelector } from 'store/UserSession';
import {
  getAllKeywordResults,
  getStatusLabel,
  normalizeKeywordStatus,
  KEYWORD_STATUS,
} from 'pages/AdminClient/GenerateKeyword/keywordDiscoveryApi';

const ADMIN_CLIENT_KEYWORD_DISCOVERY_PAGE = '/AdminClient/KeywordDiscovery';
const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const normalizeUrl = (input) =>
  (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const statusBadgeStyle = (status) => {
  const normalized = normalizeKeywordStatus(status) || KEYWORD_STATUS.IN_PROCESS;
  if (normalized === KEYWORD_STATUS.IN_PROCESS) {
    return { background: '#eef2ff', color: '#4338ca' };
  }
  if (normalized === KEYWORD_STATUS.DONE) {
    return { background: '#ecfdf3', color: '#027a48' };
  }
  if (normalized === KEYWORD_STATUS.FAILED) {
    return { background: '#fef3f2', color: '#b42318' };
  }
  return { background: '#f3f4f6', color: '#6b7280' };
};

const KeywordDiscovery = () => {
  usePageTracking(ADMIN_CLIENT_KEYWORD_DISCOVERY_PAGE);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const pollTimerRef = useRef(null);

  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients } = useSelector(adminClientClientListSelector) || {};
  const { user } = useSelector(UserSessionSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { currentPage, setCurrentPage, totalPages, paginatedItems } = usePagination(
    rows,
    itemsPerPage
  );

  const selectedWebsite = useMemo(() => {
    if (!selectedWebsiteUrl || clientsList.length === 0) return null;
    const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
    return (
      clientsList.find((website) => {
        const wUrl = website?.urlSite || website?.url || '';
        const wName = website?.name || '';
        return (
          wUrl === selectedWebsiteUrl ||
          wName === selectedWebsiteUrl ||
          normalizeUrl(wUrl) === normalizedSaved ||
          normalizeUrl(wName) === normalizedSaved
        );
      }) || null
    );
  }, [clientsList, selectedWebsiteUrl]);

  const analysisContext = useMemo(() => {
    const websiteUrl =
      selectedWebsite?.urlSite || selectedWebsite?.url || selectedWebsiteUrl || '';
    const account =
      selectedWebsite?.account || user?.companyName || user?.account || '';
    return {
      account: String(account || '').trim(),
      websiteUrl: String(websiteUrl || '').trim(),
    };
  }, [selectedWebsite, selectedWebsiteUrl, user]);

  useEffect(() => {
    document.title = `Typix - ${t('Découverte de mots-clés')}`;
  }, [t]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, analysisContext.account, analysisContext.websiteUrl, setCurrentPage]);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  const fetchStatuses = useCallback(async () => {
    const { account, websiteUrl } = analysisContext;
    if (!account || !websiteUrl) {
      setRows([]);
      return;
    }
    try {
      const data = await getAllKeywordResults({ account, websiteUrl });
      setRows(
        (Array.isArray(data) ? data : []).map((item, index) => ({
          id: `${item?.keyword || 'kw'}-${index}`,
          keyword: item?.keyword || '',
          status: normalizeKeywordStatus(item?.status) || item?.status || '',
          progress: item?.progress,
          message: item?.message,
        }))
      );
      setError('');
    } catch {
      setError(t("Impossible de récupérer le statut des analyses."));
    }
  }, [analysisContext, t]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!analysisContext.account || !analysisContext.websiteUrl) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      await fetchStatuses();
      if (!cancelled) setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [analysisContext.account, analysisContext.websiteUrl, fetchStatuses]);

  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    const hasInProcess = rows.some((row) => row.status === KEYWORD_STATUS.IN_PROCESS);
    if (!hasInProcess || !analysisContext.account || !analysisContext.websiteUrl) {
      return undefined;
    }

    pollTimerRef.current = setInterval(() => {
      fetchStatuses();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [rows, analysisContext.account, analysisContext.websiteUrl, fetchStatuses]);

  const goToGenerateKeyword = useCallback(
    (keyword) => {
      const kw = String(keyword || '').trim();
      if (!kw) return;
      history.push(`/AdminClient/GenerateKeyword?keyword=${encodeURIComponent(kw)}`);
    },
    [history]
  );

  const columns = useMemo(
    () => [
      {
        header: t('Mot clé'),
        accessor: 'keyword',
        sortable: false,
        renderCell: (row) => (
          <span
            style={{
              fontSize: 14,
              color: 'black',
              fontWeight: 500,
              textUnderlineOffset: 2,
              cursor: 'pointer',
            }}
          >
            {row.keyword}
          </span>
        ),
      },
      {
        header: t('Status'),
        accessor: 'status',
        sortable: false,
        width: '20%',
        renderCell: (row) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              ...statusBadgeStyle(row.status),
            }}
          >
            {getStatusLabel(row.status)}
            {row.status === KEYWORD_STATUS.IN_PROCESS &&
              Number.isFinite(Number(row.progress)) &&
              Number(row.progress) > 0 && (
                <span style={{ fontWeight: 500, opacity: 0.85 }}>
                  {Math.round(Number(row.progress))}%
                </span>
              )}
          </span>
        ),
      },
      {
        header: '',
        accessor: 'actions',
        sortable: false,
        width: '30%',
        renderCell: (row) => {
          const actionButton = (() => {
            if (row.status === KEYWORD_STATUS.DONE) {
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToGenerateKeyword(row.keyword);
                  }}
                  style={{
                    border: '1px solid #c7d2fe',
                    background: '#fff',
                    color: '#4338ca',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('Voir les résultats')}
                </button>
              );
            }
            if (row.status === KEYWORD_STATUS.FAILED) {
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToGenerateKeyword(row.keyword);
                  }}
                  style={{
                    border: '1px solid #fecaca',
                    background: '#fff',
                    color: '#b42318',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('Relancer')}
                </button>
              );
            }
            return null;
          })();

          if (!actionButton) return null;

          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              {actionButton}
            </div>
          );
        },
      },
    ],
    [t, goToGenerateKeyword]
  );

  const handleGenerateKeyword = () => {
    history.push('/AdminClient/GenerateKeyword');
  };

  return (
    <div style={{ marginTop: '-5px' }}>
      <div className="box-info-header">
        <div className="b-child-1">
          <h1>{t('Découverte de mots-clés')}</h1>
        </div>
        <div className="b-child-2 d-flex justify-content-end align-items-center">
          <button
            type="button"
            onClick={handleGenerateKeyword}
            style={{
              padding: '6px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#6458F1',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Poppins, sans-serif',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#5548d1';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6458F1';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            + {t('Générer un mot-clé')}
          </button>
        </div>
      </div>
      <hr
        style={{
          border: 'none',
          borderBottom: '1px solid rgb(179, 180, 182)',
          marginLeft: '-30px',
          marginBottom: 8,
          width: 'calc(100% + 40px)',
        }}
      />

      {error && (
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{error}</div>
      )}

      {!analysisContext.account || !analysisContext.websiteUrl ? (
        <div
          style={{
            padding: '24px 10px',
            textAlign: 'center',
            color: '#6B7280',
            fontSize: 14,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {t('Veuillez sélectionner un site web pour voir les analyses.')}
        </div>
      ) : (
        <>
          {rows.length > 0 && (
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ marginBottom: 8 }}
            >
              <span className="me-2" style={{ fontSize: 13, color: '#6B7280' }}>
                {t('Afficher les résultats')}
              </span>
              <div className="dropdown" style={{ width: '70px' }}>
                <button
                  className="btn dropdown-toggle dropdown-custom w-100"
                  type="button"
                  id="keywordDiscoveryPageSize"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {itemsPerPage}
                  <span>
                    <ChevronDownIcon />
                  </span>
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="keywordDiscoveryPageSize"
                >
                  {PAGE_SIZE_OPTIONS.filter((nb) => nb !== itemsPerPage).map(
                    (item) => (
                      <li key={item}>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => setItemsPerPage(item)}
                        >
                          {item}
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          )}

          <DataTable
            headerClassName="ai-smart-table-head"
            columns={columns}
            data={loading && rows.length === 0 ? [] : paginatedItems}
            onRowClick={(row) => goToGenerateKeyword(row.keyword)}
            emptyStateContent={
              <div
                style={{
                  padding: '10px 10px',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: 14,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {loading
                  ? t('Chargement…')
                  : t('Aucun mot-clé pour le moment.')}
              </div>
            }
          />

          {rows.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              onNext={() =>
                setCurrentPage((prev) =>
                  Math.min(Math.max(1, totalPages), prev + 1)
                )
              }
              disablePrev={currentPage === 1}
              disableNext={
                loading ||
                rows.length === 0 ||
                currentPage >= Math.max(1, totalPages)
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default KeywordDiscovery;
